<template>
  <div v-if="editor" class="overlay" ref="overlay" @mousedown="onToolbarMouseDown">
    <template v-if="isStyleShown">
      <div class="text-style-element" @click="onClickTextStyle">
        <div class="text-style-text">{{ $t(`textEditor.${activeTextStyle}`) }}</div>
        <n-icon class="info-icon" icon="chevron-down" size="xs" />
      </div>
      <div class="divider"></div>
    </template>

    <TextStyleDropdown :textStyle="textStyle" @onClick="applyTextStyle" ref="menu" />

    <n-tooltip v-if="isBoldShown" :tippyProps="tooltips.bold">
      <n-editor-button
        :active="isBoldActive"
        :disabled="!canFormatText"
        :aria-label="tooltips.bold.content"
        @click="toggleBold()"
        class="styling-element"
        high
      >
        <b>B</b>
      </n-editor-button>
    </n-tooltip>
    <n-tooltip v-if="isItalicShown" :tippyProps="tooltips.italic">
      <n-editor-button
        :active="isItalicActive"
        :disabled="!canFormatText"
        :aria-label="tooltips.italic.content"
        @click="toggleItalic()"
        class="styling-element"
        high
      >
        <i>I</i>
      </n-editor-button>
    </n-tooltip>
    <n-tooltip v-if="isUnderlinedShown" :tippyProps="tooltips.underline">
      <n-editor-button
        :active="isUnderlineActive"
        :disabled="!canFormatText"
        :aria-label="tooltips.underline.content"
        @click="toggleUnderline()"
        class="styling-element"
        high
      >
        <u>U</u>
      </n-editor-button>
    </n-tooltip>
    <n-tooltip v-if="isStrikeShown" :tippyProps="tooltips.strike">
      <n-editor-button
        :active="isStrikeActive"
        :disabled="!canFormatText"
        :aria-label="tooltips.strike.content"
        @click="toggleStrike()"
        class="styling-element"
        high
      >
        <s>S</s>
      </n-editor-button>
    </n-tooltip>

    <template v-if="isAlignShown">
      <div class="divider"></div>
      <n-tooltip :tippyProps="tooltips.alignLeft">
        <n-editor-button
          :active="isTextAlignLeftActive"
          :aria-label="tooltips.alignLeft.content"
          @click="setTextAlign('left')"
          class="styling-element"
          high
        >
          <n-icon size="s" icon="align-left" />
        </n-editor-button>
      </n-tooltip>
      <n-tooltip :tippyProps="tooltips.alignCenter">
        <n-editor-button
          :active="isTextAlignCenterActive"
          :aria-label="tooltips.alignCenter.content"
          @click="setTextAlign('center')"
          class="styling-element"
          high
        >
          <n-icon size="s" icon="align-center" />
        </n-editor-button>
      </n-tooltip>
      <n-tooltip :tippyProps="tooltips.alignRight">
        <n-editor-button
          :active="isTextAlignRightActive"
          :aria-label="tooltips.alignRight.content"
          @click="setTextAlign('right')"
          class="styling-element"
          high
        >
          <n-icon size="s" icon="align-right" />
        </n-editor-button>
      </n-tooltip>
      <n-tooltip :tippyProps="tooltips.alignJustify">
        <n-editor-button
          :active="isTextAlignJustifyActive"
          :aria-label="tooltips.alignJustify.content"
          @click="setTextAlign('justify')"
          class="styling-element"
          high
        >
          <n-icon size="s" icon="align-justify" />
        </n-editor-button>
      </n-tooltip>
    </template>

    <div v-if="isLinkShown && isAnyListShown" class="divider"></div>
    <n-tooltip v-if="isOrderedListShown" :tippyProps="tooltips.orderedList">
      <n-editor-button
        :active="isOrderedListActive"
        :aria-label="tooltips.orderedList.content"
        @click="toggleOrderedList()"
        class="styling-element"
        high
      >
        <n-icon size="s" icon="ordered-list" />
      </n-editor-button>
    </n-tooltip>
    <n-tooltip v-if="isBulletListShown" :tippyProps="tooltips.bulletList">
      <n-editor-button
        :active="isBulletListActive"
        :aria-label="tooltips.bulletList.content"
        @click="toggleBulletList()"
        class="styling-element"
        high
      >
        <n-icon size="s" icon="bullet-list" />
      </n-editor-button>
    </n-tooltip>
    <n-tooltip v-if="isTaskListShown" :tippyProps="tooltips.taskList">
      <n-editor-button
        :active="isTaskListActive"
        :aria-label="tooltips.taskList.content"
        @click="toggleTaskList()"
        class="styling-element"
        high
      >
        <n-icon size="s" icon="checklist" />
      </n-editor-button>
    </n-tooltip>
    <div v-if="isLinkShown" class="divider"></div>
    <n-tooltip v-if="isLinkShown" :tippyProps="tooltips.link">
      <n-editor-button
        :active="isLinkActive"
        :aria-label="tooltips.link.content"
        @click="openLinkPopup()"
        class="styling-element"
        ref="linkbutton"
        high
      >
        <n-icon icon="link" size="s" />
      </n-editor-button>
    </n-tooltip>
    <n-tooltip v-if="isQuoteShown" :tippyProps="tooltips.quote">
      <n-editor-button
        :active="isBlockquoteActive"
        :aria-label="tooltips.quote.content"
        @click="toggleBlockquote()"
        class="styling-element"
        high
      >
        <n-icon icon="quote" size="s" />
      </n-editor-button>
    </n-tooltip>
    <LinkInput :link="linkValue" @updateLink="updateLink" @removeLink="removeLink" ref="linkInput" class="linkinput" />
  </div>
</template>

<script lang="ts">
import { ChainedCommands, Editor } from '@tiptap/core'
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator'
import NEditorButton from './core/NEditorButton.vue'
import LinkInput from './LinkInput.vue'
import { Action } from './TextEditorActions.vue'
import TextStyleDropdown from './TextStyleDropdown.vue'
import { liftListsOut } from '@/util/multi-element-text-format'
import { normalizeLinkHref, selectedLinkHref } from '@/util/link-href'
import { modifierKeyLabel } from '@/util/modifier-key'
import { Props as TippyProps } from 'tippy.js'

export enum TextType {
  H1 = 'H1',
  H2 = 'H2',
  H3 = 'H3',
  PARAGRAPH = 'PARAGRAPH',
  ORDERED_LIST = 'ORDERED_LIST',
  BULLET_LIST = 'BULLET_LIST',
  TASK_LIST = 'TASK_LIST'
}

export enum TextStyle {
  H1 = 'H1',
  H2 = 'H2',
  H3 = 'H3',
  PARAGRAPH = 'PARAGRAPH',
  SMALL_PARAGRAPH = 'SMALL_PARAGRAPH'
}

@Component({
  components: {
    LinkInput,
    TextStyleDropdown
  }
})
export default class TextStylingBar extends Vue {
  @Prop() private editor!: Editor
  @Prop({ default: () => [] }) private targetEditors!: Editor[]
  @Prop({ default: () => [] }) private hideActions!: Action[]
  @Prop({ default: () => [] }) private hideTextTypes!: TextType[]
  private linkValue = ''
  @Ref('linkInput') inputRef!: LinkInput
  @Ref('linkbutton') linkbuttonRef!: NEditorButton
  @Ref('overlay') overlayRef!: HTMLElement
  @Ref('menu') menuRef!: TextStyleDropdown

  private textStyle = TextStyle
  private style = TextStyle.PARAGRAPH
  /** The editor is not reactive, so its selection is mirrored here to re-render when a caret becomes a selection */
  private selectionEmpty = true

  @Watch('editor', { immediate: true })
  onEditorChanged(editor?: Editor, previous?: Editor) {
    previous?.off('transaction', this.syncSelection)
    editor?.on('transaction', this.syncSelection)
    this.syncSelection()
  }

  beforeDestroy() {
    this.editor?.off('transaction', this.syncSelection)
  }

  syncSelection() {
    this.selectionEmpty = !this.editor || this.editor.isDestroyed || this.editor.state.selection.empty
  }

  get isMultiTarget(): boolean {
    return this.targetEditors.length > 1
  }

  get applicableEditors(): Editor[] {
    const editors = this.isMultiTarget ? this.targetEditors : this.editor ? [this.editor] : []
    return editors.filter(editor => !!editor && !editor.isDestroyed)
  }

  /** An option the editor's schema cannot apply does nothing when clicked, so it is not offered */
  private canApplyMark(name: string): boolean {
    return this.applicableEditors.every(editor => !!editor.schema.marks[name])
  }

  private canApplyNode(name: string): boolean {
    return this.applicableEditors.every(editor => !!editor.schema.nodes[name])
  }

  /**
   * Bold, italic, underline and strike format selected text: with only a caret (the bubble also shows in a link) they
   * would just arm a style for the next keystroke. The multi-element toolbar formats whole elements, so it is exempt.
   */
  get canFormatText(): boolean {
    if (this.isMultiTarget) return true
    return !this.selectionEmpty
  }

  get isStyleShown() {
    return this.canApplyNode('heading')
  }

  get isAlignShown() {
    return this.applicableEditors.every(editor => editor.extensionManager.extensions.some(e => e.name === 'textAlign'))
  }

  get isAnyListShown() {
    return this.isOrderedListShown || this.isBulletListShown || this.isTaskListShown
  }

  /**
   * One cached object per option: NTooltip rebuilds its tippy instance whenever tippyProps changes identity, and a
   * getter is only re-evaluated when the locale changes.
   */
  get tooltips(): Record<string, Partial<TippyProps>> {
    const t = (key: string, values?: Record<string, string>) => this.$t(`textEditor.tooltip.${key}`, values) as string
    const names = ['bold', 'italic', 'underline', 'strike', 'alignLeft', 'alignCenter', 'alignRight', 'alignJustify']
    const result: Record<string, Partial<TippyProps>> = {}
    for (const name of [...names, 'orderedList', 'bulletList', 'taskList', 'quote']) {
      result[name] = { content: t(name) }
    }
    result.link = { content: t('link', { key: modifierKeyLabel() }) }
    return result
  }

  get isBoldShown() {
    return this.canApplyMark('bold') && !this.hideActions.includes(Action.BOLD)
  }

  get isItalicShown() {
    return this.canApplyMark('italic') && !this.hideActions.includes(Action.ITALIC)
  }

  get isUnderlinedShown() {
    return this.canApplyMark('underline') && !this.hideActions.includes(Action.UNDERLINED)
  }

  get isStrikeShown() {
    return this.canApplyMark('strike') && !this.hideActions.includes(Action.STRIKE)
  }

  get isOrderedListShown() {
    return this.canApplyNode('orderedList') && !this.hideTextTypes.includes(TextType.ORDERED_LIST)
  }

  get isBulletListShown() {
    return this.canApplyNode('bulletList') && !this.hideTextTypes.includes(TextType.BULLET_LIST)
  }

  get isTaskListShown() {
    return this.canApplyNode('taskList') && !this.hideTextTypes.includes(TextType.TASK_LIST)
  }

  get isLinkShown() {
    return this.canApplyMark('link') && !this.hideActions.includes(Action.LINK)
  }

  get isQuoteShown() {
    return this.canApplyNode('blockquote') && !this.hideActions.includes(Action.BLOCKQUOTE)
  }

  get isBoldActive() {
    return this.applicableEditors.every(editor => editor.isActive('bold'))
  }

  get isItalicActive() {
    return this.applicableEditors.every(editor => editor.isActive('italic'))
  }

  get isUnderlineActive() {
    return this.applicableEditors.every(editor => editor.isActive('underline'))
  }

  get isStrikeActive() {
    return this.applicableEditors.every(editor => editor.isActive('strike'))
  }

  get isOrderedListActive() {
    return this.applicableEditors.every(editor => editor.isActive('orderedList'))
  }

  get isBulletListActive() {
    return this.applicableEditors.every(editor => editor.isActive('bulletList'))
  }

  get isTaskListActive() {
    return this.applicableEditors.every(editor => editor.isActive('taskList'))
  }

  get isLinkActive() {
    return this.applicableEditors.every(editor => editor.isActive('link'))
  }

  get isBlockquoteActive() {
    return this.applicableEditors.every(editor => editor.isActive('blockquote'))
  }

  get isHeadingActive() {
    return this.editor.isActive('heading')
  }

  get isHeading1Active() {
    return this.applicableEditors.every(editor => editor.isActive('heading', { level: 1 }))
  }

  get isHeading2Active() {
    return this.applicableEditors.every(editor => editor.isActive('heading', { level: 2 }))
  }

  get isHeading3Active() {
    return this.applicableEditors.every(editor => editor.isActive('heading', { level: 3 }))
  }

  get isSmallParagraphActive() {
    return this.applicableEditors.every(editor => editor.isActive('paragraph', { class: 'small' }))
  }

  get activeTextStyle() {
    switch (true) {
      case this.isHeading1Active:
        return TextStyle.H1
      case this.isHeading2Active:
        return TextStyle.H2
      case this.isHeading3Active:
        return TextStyle.H3
      case this.isSmallParagraphActive:
        return TextStyle.SMALL_PARAGRAPH
      default:
        return TextStyle.PARAGRAPH
    }
  }

  get isTextAlignLeftActive() {
    return this.isTextAlignActive('left')
  }

  get isTextAlignCenterActive() {
    return this.isTextAlignActive('center')
  }

  get isTextAlignRightActive() {
    return this.isTextAlignActive('right')
  }

  get isTextAlignJustifyActive() {
    return this.isTextAlignActive('justify')
  }

  private isTextAlignActive(align: string): boolean {
    return this.applicableEditors.every(
      editor => editor.isActive('paragraph', { textAlign: align }) || editor.isActive('heading', { textAlign: align })
    )
  }

  private runCommand(command: (chain: ChainedCommands) => ChainedCommands): void {
    for (const editor of this.applicableEditors) {
      if (this.isMultiTarget) {
        // Apply to whole doc, then collapse + blur so TipTap BubbleMenu does not appear.
        command(editor.chain().selectAll()).run()
        this.finishMultiEditorCommand(editor)
      } else {
        command(editor.chain().focus()).run()
      }
    }
  }

  private finishMultiEditorCommand(editor: Editor): void {
    if (editor.isDestroyed) {
      return
    }
    const docSize = editor.state.doc.content.size
    if (docSize > 0) {
      const pos = Math.min(1, docSize)
      editor.chain().setTextSelection(pos).run()
    }
    editor.view.dom.blur()
  }

  /**
   * selectAll + toggle*List often fails to unwrap lists across a whole doc.
   * Decide one on/off state for the selection, then enable via toggle or disable via liftListItem.
   */
  /**
   * Per-editor toggle* inverts mixed selections. Decide one on/off state for the group, then set or
   * unset on each target — same approach as lists and blockquote.
   */
  private toggleMarkForTargets(
    isActive: (editor: Editor) => boolean,
    enable: (chain: ChainedCommands) => ChainedCommands,
    disable: (chain: ChainedCommands) => ChainedCommands,
    singleToggle: (chain: ChainedCommands) => ChainedCommands
  ): void {
    if (!this.canFormatText) return
    if (!this.isMultiTarget) {
      this.runCommand(singleToggle)
      return
    }

    const editors = this.applicableEditors
    if (editors.length === 0) {
      return
    }

    const shouldEnable = !editors.every(isActive)
    for (const editor of editors) {
      const active = isActive(editor)
      if (shouldEnable && !active) {
        enable(editor.chain().selectAll()).run()
      } else if (!shouldEnable && active) {
        disable(editor.chain().selectAll()).run()
      }
      this.finishMultiEditorCommand(editor)
    }
  }

  private toggleListForTargets(
    listName: 'orderedList' | 'bulletList' | 'taskList',
    toggle: (chain: ChainedCommands) => ChainedCommands
  ): void {
    if (!this.isMultiTarget) {
      this.runCommand(toggle)
      return
    }

    const editors = this.applicableEditors
    if (editors.length === 0) {
      return
    }

    const shouldEnable = !editors.every(editor => editor.isActive(listName))
    const listItemName = listName === 'taskList' ? 'taskItem' : 'listItem'

    for (const editor of editors) {
      const isActive = editor.isActive(listName)
      if (shouldEnable) {
        if (!isActive) {
          toggle(editor.chain().selectAll()).run()
        }
      } else if (isActive) {
        liftListsOut(editor, listName, listItemName)
      }
      this.finishMultiEditorCommand(editor)
    }
  }

  toggleBold() {
    this.toggleMarkForTargets(
      editor => editor.isActive('bold'),
      chain => chain.setBold(),
      chain => chain.unsetBold(),
      chain => chain.toggleBold()
    )
  }

  toggleItalic() {
    this.toggleMarkForTargets(
      editor => editor.isActive('italic'),
      chain => chain.setItalic(),
      chain => chain.unsetItalic(),
      chain => chain.toggleItalic()
    )
  }

  toggleUnderline() {
    this.toggleMarkForTargets(
      editor => editor.isActive('underline'),
      chain => chain.setUnderline(),
      chain => chain.unsetUnderline(),
      chain => chain.toggleUnderline()
    )
  }

  toggleStrike() {
    this.toggleMarkForTargets(
      editor => editor.isActive('strike'),
      chain => chain.setStrike(),
      chain => chain.unsetStrike(),
      chain => chain.toggleStrike()
    )
  }

  setTextAlign(align: 'left' | 'center' | 'right' | 'justify') {
    this.runCommand(chain => chain.setTextAlign(align))
  }

  toggleOrderedList() {
    this.toggleListForTargets('orderedList', chain => chain.toggleOrderedList())
  }

  toggleBulletList() {
    this.toggleListForTargets('bulletList', chain => chain.toggleBulletList())
  }

  toggleTaskList() {
    this.toggleListForTargets('taskList', chain => chain.toggleTaskList())
  }

  toggleBlockquote() {
    if (!this.isMultiTarget) {
      this.runCommand(chain => chain.toggleBlockquote())
      return
    }

    const editors = this.applicableEditors
    if (editors.length === 0) {
      return
    }

    const shouldEnable = !editors.every(editor => editor.isActive('blockquote'))
    for (const editor of editors) {
      const isActive = editor.isActive('blockquote')
      if (shouldEnable && !isActive) {
        editor.chain().selectAll().toggleBlockquote().run()
      } else if (!shouldEnable && isActive) {
        editor.chain().focus('start').toggleBlockquote().run()
      }
      this.finishMultiEditorCommand(editor)
    }
  }

  openLinkPopup() {
    this.linkValue = selectedLinkHref(this.editor)

    this.inputRef.open(this.linkbuttonRef.$el as HTMLElement)
  }

  updateLink(link: string) {
    if (!link) {
      this.removeLink()
      return
    }
    link = normalizeLinkHref(link)
    if (this.isMultiTarget) {
      this.runCommand(chain => chain.setLink({ href: link }))
      return
    }
    this.editor.chain().focus().extendMarkRange('link').setLink({ href: link }).run()
  }

  removeLink() {
    this.runCommand(chain => chain.unsetLink())
  }

  onToolbarMouseDown(event: MouseEvent) {
    event.preventDefault()
  }

  onClickTextStyle() {
    this.menuRef.open(this.overlayRef)
  }

  applyTextStyle(style: string) {
    for (const editor of this.applicableEditors) {
      const isHeadingActive = editor.isActive('heading')
      const currentAttributes = editor.getAttributes(isHeadingActive ? 'heading' : 'paragraph')
      let chain = this.isMultiTarget ? editor.chain().selectAll() : editor.chain().focus()

      switch (style) {
        case 'PARAGRAPH':
          chain.setNode('paragraph', { ...currentAttributes, class: '' }).run()
          break
        case 'SMALL_PARAGRAPH':
          chain.setNode('paragraph', { ...currentAttributes, class: 'small' }).run()
          break
        case 'H1':
          chain.setNode('heading', { ...currentAttributes, level: 1 }).run()
          break
        case 'H2':
          chain.setNode('heading', { ...currentAttributes, level: 2 }).run()
          break
        case 'H3':
          chain.setNode('heading', { ...currentAttributes, level: 3 }).run()
          break
        default:
          break
      }

      if (this.isMultiTarget) {
        this.finishMultiEditorCommand(editor)
      }
    }
    this.menuRef.close()
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;

.overlay {
  @include notch-style-floating;
  @include notch-text-label-strong-M;
  background-color: $notch-color-neutral-grey-000;
  border-radius: $notch-radius-2x;
  display: inline-flex;
  flex-wrap: wrap;
  row-gap: $notch-space-1x;
  max-width: calc(100vw - 16px);
  color: $notch-color-neutral-grey-900;
  padding: $notch-space-2x $notch-space-2_5x;
  cursor: pointer;
}
.linkinput {
  margin-top: $notch-space-1x;
}

.divider {
  width: 1px;
  background-color: $notch-color-neutral-sand-200;
  margin-left: $notch-space-2x;
  margin-right: $notch-space-2x;
}

.styling-element {
  width: $notch-space-6x;
  height: $notch-space-6x;
  padding: $notch-space-1x;
}
.text-style-element {
  display: flex;
  gap: $notch-space-1_5x;
  padding: $notch-space-1x;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;

  .info-icon {
    color: $notch-color-neutral-sand-600;
    padding: $notch-space-1x;
  }
  .text-style-text {
    @include notch-text-label-light-M;
  }
}
</style>
